import type { CascaderOption } from '@/components';

export type Recursive<T> = T & {
  children: Array<Recursive<T>> | null;
};

/**
 * フラットな配列データを親子関係を持つ再帰的な階層構造に変換する
 * @param data 変換対象のデータ配列
 * @param org 全体のオリジナルデータ配列（子要素を探すために使用）
 * @returns 階層構造を持つデータ配列
 */
export const getRecursiveArray = <
  T extends { id: string; upperId: string | null },
>(
  data: T[],
  org: T[],
): Array<Recursive<T>> => {
  return data.flatMap((d) => {
    if (org.length === 0) {
      return [];
    }
    const children = org.filter((o) => o.upperId === d.id);
    if (children.length === 0) {
      return {
        ...d,
        children: null,
      };
    }
    return {
      ...d,
      children: getRecursiveArray(children, org),
    };
  });
};

/**
 * 指定したIDの要素から親要素を再帰的に辿り、ルートまでの経路を返す
 * @param id 検索対象のID
 * @param data 全体のデータ配列
 * @param current 現在の経路（再帰処理用）
 * @returns ルートから指定要素までの経路配列
 */
export const findUpperRecursive = <
  T extends { id: string; upperId: string | null },
>(
  id: string,
  data: T[],
  current: T[] = [],
): T[] => {
  const target = data.find((d) => d.id === id);
  if (!target) {
    return current;
  }
  const upper = data.find((d) => d.id === target.upperId);
  if (!upper) {
    return [...current].concat(target);
  }

  return findUpperRecursive(upper.id, data, [...current].concat(target));
};

/**
 * 階層構造データをCascaderやTreeSelectなどのUIコンポーネント用のオプション形式に変換する
 * @param data 階層構造を持つデータ配列
 * @param value valueとして使用するキー
 * @param label labelとして使用するキー
 * @returns value/label形式に変換された階層構造データ
 */
export const genRecursiveOptions = <
  T,
  R extends Recursive<T>,
  Value extends keyof R,
  Label extends keyof R,
>(
  data: R[],
  value: Value,
  label: Label,
): Array<Recursive<{ value: R[Value]; label: R[Label] }>> => {
  // @ts-expect-error
  return data.map((d) => {
    if (!d.children) {
      return {
        value: d[value],
        label: d[label],
        children: null,
      };
    }
    return {
      value: d[value],
      label: d[label],
      // @ts-expect-error
      children: genRecursiveOptions(d.children, value, label),
    };
  });
};

/**
 * ネストされた親要素（upper）を持つオブジェクトを配列形式に変換する
 * @param data 親要素を含むオブジェクト
 * @param current 現在の配列（再帰処理用）
 * @returns 最上位から現在の要素までの配列
 */
// TODO:
// BE will change upper as recursive but not completed yet,
// FE has to change the type from any to the certain one after then.
export const convertUpperToArray = <T extends object & { upper?: any }>(
  data: T,
  current: T[] = [],
): T[] => {
  if (!data.upper) {
    return [...current, data];
  }
  return convertUpperToArray(data.upper, [...current, data]);
};

/**
 * 階層構造データから子要素を持つ親要素のIDのみを抽出する
 * @param list 階層構造を持つデータ配列
 * @returns 子要素を持つ親要素のID配列
 */
export const getRecursiveIds = <T extends { id: string }>(
  list: Array<Recursive<T>>,
) => {
  const result = list.reduce<string[]>((acc, cur) => {
    const recursive = (data: Recursive<T>) => {
      if (!data.children?.length) {
        return;
      }

      acc.push(data.id);
      for (const child of data.children) {
        recursive(child);
      }
    };

    recursive(cur);

    return acc;
  }, []);

  return result;
};

/**
 * descendantキーを持つデータをchildrenキーを持つ階層構造に変換する
 * @param data descendantキーを持つデータ配列
 * @returns childrenキーを持つ階層構造データ
 */
export const transformToDataWithChildren = <T extends { descendant: T[] }>(
  data: T[],
): Array<Recursive<T>> => {
  return data.map((d) => ({
    ...d,
    children:
      d.descendant.length === 0
        ? null
        : transformToDataWithChildren(d.descendant),
  }));
};

/**
 * 階層構造データから指定したキーと値に一致する要素のみを再帰的にフィルタリングする
 * @param data 階層構造を持つデータ配列
 * @param key フィルタリング対象のキー
 * @param value フィルタリング対象の値
 * @returns フィルタリングされた階層構造データ
 */
export const filterRecursively = <T>(
  data: Array<Recursive<T>>,
  { key, value }: { key: string; value: any },
): Array<Recursive<T>> => {
  return data.flatMap((d) => {
    return key in d && d[key] === value
      ? d.children?.length
        ? { ...d, children: filterRecursively(d.children, { key, value }) }
        : d
      : [];
  });
};

/**
 * 配列からID一覧と期間（開始年/終了年）の最大・最小値を抽出する
 * @param arr ID、開始年、終了年を持つオブジェクトの配列
 * @returns ID配列、最大開始年、最小終了年を含むオブジェクト
 */
export const extractIdsAndYearPeriod = <
  T extends { id: string; startYear: number | null; endYear: number | null },
>(
  arr: T[] | undefined,
) => ({
  ids: arr?.map((v) => v.id),
  startYear: Math.max(...(arr?.flatMap((v) => v.startYear ?? []) ?? [])),
  endYear: Math.min(...(arr?.flatMap((v) => v.endYear ?? []) ?? [])),
});

/**
 * 階層構造データから指定したレベルまでの要素を抽出し、それより深い階層を削除する
 * @param data 階層構造を持つデータ配列
 * @param upperLevel 保持する最大レベル
 * @returns 指定レベルまでに制限された階層構造データ
 */
export const extractLevelAndRemoveChildren = <T extends { level: number }>(
  data: Array<Recursive<T>>,
  upperLevel: number | undefined,
): Array<Recursive<T>> => {
  if (!upperLevel) {
    return data;
  }
  return data.flatMap((recursive) => {
    if (recursive.level === upperLevel) {
      return { ...recursive, children: null };
    }
    if (!recursive.children) {
      return [];
    }
    return {
      ...recursive,
      children: extractLevelAndRemoveChildren(recursive.children, upperLevel),
    };
  });
};

/**
 * Cascaderデータから選択されたIDまでの経路（親ID含む）を取得する
 * @param data Cascaderオプションデータ
 * @param selectedIds 選択されたID配列
 * @param parent 親要素の経路（再帰処理用）
 * @returns 選択された各要素までの経路配列の配列
 */
export const getSelectedCascaderIds = (
  data: CascaderOption[],
  selectedIds: string[],
  parent: string[] | undefined = [],
) => {
  const result = data.reduce<string[][]>((acc, item) => {
    const data = [...parent, item.value.toString()];
    if (selectedIds.includes(item.value.toString())) {
      acc.push([...data]);
    } else if (item.children?.length) {
      acc.push(...getSelectedCascaderIds(item.children, selectedIds, data));
    }
    return acc;
  }, []);
  return result;
};

/**
 * 階層構造データから指定したIDを持つ要素までの経路を検索する
 * @param id 検索対象のID
 * @param data 階層構造を持つデータ配列
 * @param key IDを格納しているキー名
 * @returns ルートから指定要素までのID配列
 */
export const findChildrenRecursive = <T, K extends keyof T>(
  id: string,
  data: Recursive<T>[],
  key: K,
): string[] => {
  for (const d of data) {
    if (key in d) {
      const currentValue = d[key];
      if (typeof currentValue === 'string') {
        if (currentValue === id) {
          return [id];
        }
        if (d.children && d.children.length > 0) {
          const children = findChildrenRecursive(id, d.children, key);
          if (children.length > 0) {
            return [currentValue, ...children];
          }
        }
      }
    }
  }

  return [];
};

/**
 * CascaderオプションデータをフラットなIDとラベルの配列に変換し、各要素に親IDの経路を付与する
 * @param data Cascaderオプションデータ
 * @param upperIds 親IDの経路（再帰処理用）
 * @returns ID、ラベル、親IDの経路を持つフラットな配列
 */
export const transformRecursiveDataWithUpper = (
  data: CascaderOption[],
  upperIds: string[] = [],
): Array<{ id: string; label: string; upperIdsPath: string[] }> => {
  const newData: Array<{ id: string; label: string; upperIdsPath: string[] }> =
    [];
  data.map((item) => {
    newData.push({
      id: item.value.toString(),
      label: item.label,
      upperIdsPath: upperIds,
    });
    if (item.children?.length) {
      newData.push(
        ...transformRecursiveDataWithUpper(item.children, [
          ...upperIds,
          item.value.toString(),
        ]),
      );
    }
  });
  return newData;
};

/**
 * 配列から指定したキーの値が重複する要素を削除する（最初に出現した要素を残す）
 * @param array 処理対象の配列
 * @param key 重複判定に使用するキー
 * @returns 重複を除去した配列
 */
export const removeDuplicatesByKey = <T extends object, K extends keyof T>(
  array: T[],
  key: K,
): T[] => {
  return array.filter(
    (element, index, self) =>
      self.findIndex((e) => e[key] === element[key]) === index,
  );
};

/**
 * Cascaderデータから末端要素（子要素を持たない要素）のIDのみを抽出する
 * @param data Cascaderオプションデータ
 * @returns 末端要素のID配列
 */
export const extractLeafNodeIds = (data: CascaderOption[] = []): string[] => {
  return data.reduce<string[]>((acc, curr) => {
    if (curr.children?.length) {
      acc.push(...extractLeafNodeIds(curr.children));
    } else {
      acc.push(curr.value.toString());
    }
    return acc;
  }, []);
};

/**
 * ネストされた配列をフラットな状態に変換する
 * @param orgList 処理対象の配列
 * @param nestedKey 子要素を格納しているキー名
 * @returns フラット化された配列（nestedKeyプロパティは除外される）
 */
export const flattenList = <T, K extends keyof T>(
  orgList: T[] | undefined,
  nestedKey: K,
): Omit<T, K>[] => {
  if (!orgList) {
    return [];
  }

  const flattenRecursively = (list: T): Omit<T, K>[] => {
    const nestedItems = list[nestedKey];
    const descendants =
      Array.isArray(nestedItems) && nestedItems.length > 0
        ? nestedItems.flatMap((item) => flattenRecursively(item as T))
        : [];

    const { [nestedKey]: _, ...rest } = list;
    return [rest as Omit<T, K>, ...descendants];
  };

  return orgList.flatMap(flattenRecursively);
};
